import { useMemo, useState } from 'react';
import { useFinance } from '@/hooks/useFinance';
import { formatDate, formatMoney } from '@/lib/finance';
import { FinanceSection, FinanceEmpty, FinanceModal, Field, inputClass, FinanceStatCard } from '../components/FinanceUI';

export default function FinanceFournisseursPage() {
  const finance = useFinance();
  const { currency, suppliers, supplierTransactions, accounts, loading } = finance;

  const [supplierOpen, setSupplierOpen] = useState(false);
  const [txOpen, setTxOpen] = useState<{ supplierId: number; type: 'invoice' | 'payment' } | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');

  const [txAmount, setTxAmount] = useState('');
  const [txDue, setTxDue] = useState('');
  const [txDesc, setTxDesc] = useState('');
  const [txAccount, setTxAccount] = useState('');
  const [txMethod, setTxMethod] = useState('Virement bancaire');

  const totalPayable = useMemo(
    () => suppliers.reduce((s, sup) => s + Math.max(0, finance.supplierBalance(sup.id)), 0),
    [suppliers, finance]
  );

  const submitSupplier = async () => {
    if (!name.trim()) { setMsg('Nom requis'); return; }
    setBusy(true);
    try {
      await finance.createSupplier({ name: name.trim(), phone: phone || null, email: email || null, address: address || null });
      setName(''); setPhone(''); setEmail(''); setAddress('');
      setSupplierOpen(false);
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'Erreur');
    } finally { setBusy(false); }
  };

  const submitTx = async () => {
    if (!txOpen) return;
    const value = parseFloat(txAmount);
    if (!value || value <= 0) { setMsg('Montant invalide'); return; }

    // Règle de conformité : les règlements en espèces à un même fournisseur
    // sont plafonnés à 5 000 DH par jour (au-delà : virement, chèque barré
    // ou effet de commerce).
    if (txOpen.type === 'payment' && txMethod === 'Espèces') {
      const today = new Date().toISOString().slice(0, 10);
      const paidToday = supplierTransactions
        .filter((t) => t.supplier_id === txOpen.supplierId && t.type === 'payment')
        .filter((t) => String(t.created_at || '').slice(0, 10) === today)
        .reduce((s, t) => s + (Number(t.amount) || 0), 0);
      if (paidToday + value > 5000) {
        setMsg(
          `Paiement en espèces refusé : plafond de 5 000 DH par jour et par fournisseur (déjà ${paidToday.toLocaleString('fr-FR')} DH aujourd’hui). Privilégiez le virement, un chèque barré ou un effet de commerce.`,
        );
        return;
      }
    }

    setBusy(true);
    try {
      await finance.addSupplierTransaction({
        supplier_id: txOpen.supplierId,
        type: txOpen.type,
        amount: value,
        due_date: txDue || null,
        description:
          (txDesc || (txOpen.type === 'invoice' ? 'Facture fournisseur' : 'Paiement fournisseur')) +
          (txOpen.type === 'payment' ? ` · ${txMethod}` : ''),
        account_id: txOpen.type === 'payment' && txAccount ? Number(txAccount) : null,
      });
      setTxAmount(''); setTxDue(''); setTxDesc(''); setTxAccount('');
      setTxOpen(null);
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'Erreur');
    } finally { setBusy(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>;
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <FinanceStatCard label="Total à payer" value={formatMoney(totalPayable, currency)} icon="ri-truck-line" tone="accent" />
        <FinanceStatCard label="Fournisseurs" value={String(suppliers.length)} icon="ri-group-line" tone="secondary" />
        <FinanceStatCard label="Factures fournisseurs" value={String(supplierTransactions.filter((t) => t.type === 'invoice').length)} icon="ri-file-text-line" tone="secondary" />
        <FinanceStatCard label="Paiements effectués" value={String(supplierTransactions.filter((t) => t.type === 'payment').length)} icon="ri-hand-coin-line" tone="primary" />
      </div>

      <button onClick={() => { setName(''); setPhone(''); setEmail(''); setAddress(''); setSupplierOpen(true); }} className="flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer">
        <i className="ri-add-line"></i>Nouveau fournisseur
      </button>

      {msg && <div className="px-4 py-2.5 rounded-lg bg-secondary-50 text-secondary-800 text-sm">{msg}</div>}

      <FinanceSection title={`Fournisseurs (${suppliers.length})`} icon="ri-truck-line">
        {suppliers.length === 0 ? (
          <FinanceEmpty icon="ri-truck-line" title="Aucun fournisseur" hint="Ajoutez vos fournisseurs pour suivre vos achats et vos dettes." />
        ) : (
          <div className="space-y-2">
            {suppliers.map((s) => {
              const bal = finance.supplierBalance(s.id);
              const txs = supplierTransactions.filter((t) => t.supplier_id === s.id);
              const isOpen = expanded === s.id;
              return (
                <div key={s.id} className="border border-background-200/70 rounded-lg overflow-hidden">
                  <div className="flex items-center gap-3 p-3 bg-background-50">
                    <span className="w-9 h-9 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center flex-shrink-0 font-bold text-sm">
                      {s.name.charAt(0).toUpperCase()}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-foreground-900 truncate">{s.name}</div>
                      <div className="text-xs text-foreground-400 truncate">{s.phone || s.email || 'Sans contact'}</div>
                    </div>
                    <span className={`text-sm font-bold whitespace-nowrap ${bal > 0 ? 'text-accent-700' : 'text-foreground-500'}`}>{formatMoney(bal, currency)}</span>
                    <button onClick={() => setTxOpen({ supplierId: s.id, type: 'invoice' })} className="px-2.5 py-1.5 rounded-full bg-accent-50 text-accent-700 text-xs font-medium whitespace-nowrap hover:bg-accent-100 transition-colors cursor-pointer">Facture</button>
                    <button onClick={() => setTxOpen({ supplierId: s.id, type: 'payment' })} className="px-2.5 py-1.5 rounded-full bg-primary-50 text-primary-700 text-xs font-medium whitespace-nowrap hover:bg-primary-100 transition-colors cursor-pointer">Paiement</button>
                    <button onClick={() => setExpanded(isOpen ? null : s.id)} className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer">
                      <i className={`${isOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-base`}></i>
                    </button>
                    <button onClick={() => finance.deleteSupplier(s.id)} className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-300 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer" title="Supprimer">
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                  {isOpen && (
                    <div className="border-t border-background-200/70 p-3 bg-background-100/50">
                      {txs.length === 0 ? (
                        <p className="text-xs text-foreground-400">Aucune facture ni paiement enregistré.</p>
                      ) : (
                        <div className="divide-y divide-background-200/70">
                          {txs.map((t) => (
                            <div key={t.id} className="flex items-center gap-2 py-2 text-xs">
                              <span className={`px-2 py-0.5 rounded-full ${t.type === 'invoice' ? 'bg-accent-100 text-accent-700' : 'bg-primary-100 text-primary-700'}`}>
                                {t.type === 'invoice' ? 'Facture' : 'Paiement'}
                              </span>
                              <span className="flex-1 min-w-0 text-foreground-700 truncate">{t.description || '—'}</span>
                              {t.due_date && <span className="text-foreground-400 whitespace-nowrap">Éch. {formatDate(t.due_date)}</span>}
                              <span className="font-semibold text-foreground-800 whitespace-nowrap">{formatMoney(Number(t.amount), currency)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </FinanceSection>

      {supplierOpen && (
        <FinanceModal title="Nouveau fournisseur" onClose={() => setSupplierOpen(false)}>
          <Field label="Nom *"><input value={name} onChange={(e) => setName(e.target.value)} className={inputClass} placeholder="Nom du fournisseur" /></Field>
          <Field label="Téléphone"><input value={phone} onChange={(e) => setPhone(e.target.value)} className={inputClass} placeholder="06..." /></Field>
          <Field label="Email"><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} placeholder="contact@fournisseur.ma" /></Field>
          <Field label="Adresse"><input value={address} onChange={(e) => setAddress(e.target.value)} className={inputClass} placeholder="Adresse" /></Field>
          <button onClick={submitSupplier} disabled={busy} className="w-full mt-2 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap">
            {busy ? <i className="ri-loader-4-line animate-spin"></i> : 'Ajouter le fournisseur'}
          </button>
        </FinanceModal>
      )}

      {txOpen && (
        <FinanceModal title={txOpen.type === 'invoice' ? 'Facture fournisseur' : 'Paiement fournisseur'} onClose={() => setTxOpen(null)}>
          <Field label="Montant *"><input type="number" min="0" step="0.01" value={txAmount} onChange={(e) => setTxAmount(e.target.value)} className={inputClass} placeholder="0.00" /></Field>
          {txOpen.type === 'invoice' && (
            <Field label="Échéance"><input type="date" value={txDue} onChange={(e) => setTxDue(e.target.value)} className={inputClass} /></Field>
          )}
          <Field label="Description"><input value={txDesc} onChange={(e) => setTxDesc(e.target.value)} className={inputClass} placeholder="Ex : Achat de stock" /></Field>
          {txOpen.type === 'payment' && (
            <Field label="Mode de règlement">
              <select value={txMethod} onChange={(e) => setTxMethod(e.target.value)} className={`${inputClass} cursor-pointer`}>
                {['Virement bancaire', 'Chèque barré', 'Effet de commerce', 'Espèces', 'Carte bancaire'].map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </Field>
          )}
          {txOpen.type === 'payment' && accounts.length > 0 && (
            <Field label="Payer depuis le compte">
              <select value={txAccount} onChange={(e) => setTxAccount(e.target.value)} className={inputClass}>
                <option value="">— Aucun compte —</option>
                {accounts.map((a) => <option key={a.id} value={String(a.id)}>{a.name}</option>)}
              </select>
            </Field>
          )}
          <button onClick={submitTx} disabled={busy} className="w-full mt-2 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap">
            {busy ? <i className="ri-loader-4-line animate-spin"></i> : 'Enregistrer'}
          </button>
          <p className="text-[11px] text-foreground-400 mt-2 flex items-start gap-1">
            <i className="ri-information-line mt-0.5"></i>
            {txOpen.type === 'invoice' ? 'Une facture augmente vos dettes fournisseurs.' : 'Un paiement réduit vos dettes et la trésorerie du compte choisi.'}
          </p>
        </FinanceModal>
      )}
    </div>
  );
}